import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const { login, verifyOtp, resendOtp, cancelOtp, isLoading, error, otpChallenge } = useAuthStore();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await login(email, password);
    if (result.success) navigate('/dashboard');
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const result = await verifyOtp(code, trustDevice);
    if (result.success) navigate('/dashboard');
  };

  const handleResend = async () => {
    const res = await resendOtp();
    if (res.success) {
      toast.success('Novo código enviado para seu e-mail');
      setCode('');
    } else {
      toast.error(res.error || 'Erro ao reenviar código');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <span className="font-bold text-3xl tracking-tight">
              OneView<span className="text-red-500">.</span>
            </span>
          </Link>
        </div>

        {otpChallenge ? (
          <Card data-testid="otp-card">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold flex items-center gap-2">
                <ShieldCheck className="h-5 w-5" /> Verificação em duas etapas
              </CardTitle>
              <CardDescription>
                Enviamos um código de 6 dígitos para {otpChallenge.email}. Ele expira em 10 minutos.
              </CardDescription>
            </CardHeader>
            <form onSubmit={handleVerify}>
              <CardContent className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <div className="space-y-2">
                  <Label htmlFor="otp">Código de acesso</Label>
                  <Input
                    id="otp"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    className="text-center text-2xl tracking-[0.5em] font-semibold"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    required
                    data-testid="otp-code-input"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="trust" checked={trustDevice}
                    onCheckedChange={setTrustDevice} data-testid="trust-device-checkbox" />
                  <label htmlFor="trust" className="text-sm cursor-pointer">
                    Confiar neste dispositivo por 15 dias
                  </label>
                </div>
              </CardContent>
              <CardFooter className="flex flex-col gap-3">
                <Button type="submit" className="w-full" disabled={isLoading || code.length < 6}
                  data-testid="otp-submit-btn">
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Confirmar código
                </Button>
                <div className="flex justify-between w-full text-sm">
                  <button type="button" className="text-primary hover:underline" onClick={handleResend}
                    data-testid="otp-resend-btn">
                    Reenviar código
                  </button>
                  <button type="button" className="text-muted-foreground hover:underline"
                    onClick={cancelOtp} data-testid="otp-cancel-btn">
                    Usar outra conta
                  </button>
                </div>
              </CardFooter>
            </form>
          </Card>
        ) : (
          <Card>
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold">Entrar</CardTitle>
              <CardDescription>
                Entre com seu email e senha para acessar
              </CardDescription>
            </CardHeader>
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="login-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Senha</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </CardContent>

              <CardFooter className="flex flex-col gap-4">
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading}
                  data-testid="login-submit-btn"
                >
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Entrar
                </Button>

                <p className="text-sm text-muted-foreground text-center">
                  Não tem uma conta?{' '}
                  <Link to="/register" className="text-primary hover:underline" data-testid="go-to-register-link">
                    Criar conta
                  </Link>
                </p>
              </CardFooter>
            </form>
          </Card>
        )}
      </div>
    </div>
  );
}
